import {
    AfterContentInit,
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    ContentChildren,
    Input,
    NgZone,
    OnDestroy,
    QueryList
} from '@angular/core';

import { BaseComponent } from '@ridango/core';

import * as PolylineUtil from 'polyline-encoded';
import { filter, takeUntil } from 'rxjs/operators';

import {
    LatLngPolylinePointType,
    LatLngPolylineType,
    MapApiService,
    PolylineOptionsType,
    PolylineType
} from '../services';
import { MapPolylinePointComponent } from './map-polyline-point.component';
import { MapPopupComponent } from './map-popup.component';
import { PolyLineStyle } from '@ridango/components';
import { Subject, timer } from 'rxjs';

@Component({
    selector: 'rwidget-map-polyline',
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapPolylineComponent extends BaseComponent implements OnDestroy, AfterContentInit {
    private mapReady$ = this.mapApi.mapReady().pipe(filter((ready) => ready));

    private polyline: PolylineType;
    private _weight = 4;
    private _encodedPath = null;
    private changes = new Subject<void>();

    private latlngs: LatLngPolylineType = [];

    @Input() color = 'blue';

    @Input() set weight(value) {
        this._weight = value;
        this.changes.next();
    }

    @Input() opacity = 1;

    @Input() set encodedPath(value) {
        this._encodedPath = value;
        this.changes.next();
    }

    @Input() fit = false;

    @Input() style: PolyLineStyle = PolyLineStyle.SOLID;

    @ContentChildren(MapPolylinePointComponent) points: QueryList<MapPolylinePointComponent>;

    @ContentChild(MapPopupComponent) popupComponent: MapPopupComponent;

    constructor(
        private mapApi: MapApiService,
        private zone: NgZone
    ) {
        super();
    }

    ngAfterContentInit() {
        this.mapReady$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => this.init());

        this.changes.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            timer(10)
                .pipe(takeUntil(this.componentDestroyed$))
                .subscribe(() => {
                    this.zone.runOutsideAngular(() => {
                        this.mapApi.removeLayer(this.polyline);
                        this.init();
                    });
                });
        });
    }

    private init(): void {
        this.zone.runOutsideAngular(() => {
            const options: PolylineOptionsType = {
                color: this.color,
                weight: this._weight,
                opacity: this.opacity,
                dashArray: this.style === PolyLineStyle.DASHED ? '5, 10' : undefined
            };

            this.latlngs = [];

            if (this._encodedPath) {
                this.latlngs = PolylineUtil.decode(this._encodedPath);
            } else {
                this.points?.forEach((point) => {
                    const latlng: LatLngPolylinePointType = [point.latitude, point.longitude];
                    this.latlngs.push(latlng);
                });

                this.onPointsChange(options);
            }

            this.polyline = this.mapApi.drawPolyline(this.latlngs, options);
            this.bindPopup();
            this.mapApi.addToMap(this.polyline);
            this.setFitIfNecessary();
        });
    }

    ngOnDestroy() {
        this.zone.runOutsideAngular(() => this.mapApi.removeLayer(this.polyline));
        super.ngOnDestroy();
    }

    private onPointsChange(options: PolylineOptionsType) {
        this.points?.changes.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            this.latlngs = [];
            this.mapApi.removeLayer(this.polyline);

            this.points.forEach((point) => {
                const latlng: LatLngPolylinePointType = [point.latitude, point.longitude];
                this.latlngs.push(latlng);
            });

            this.polyline = this.mapApi.drawPolyline(this.latlngs, options);
            this.mapApi.addToMap(this.polyline);
        });
    }

    private bindPopup(): void {
        if (this.popupComponent) {
            const popup = this.popupComponent.getPopup();
            this.mapApi.bindPopupToLayer(this.polyline, popup);
        }
    }

    private setFitIfNecessary(): void {
        if (this.fit && this.polyline) {
            this.mapApi.mapReady().subscribe((isReady) => {
                if (isReady) {
                    this.mapApi.fitBounds(this.polyline.getBounds());
                }
            });
        }
    }

    getPolyline() {
        return this.polyline;
    }
}
